/* Phase 7D-b2 — Web Push client helper.
 *
 * Handles: SW registration, VAPID key fetching, permission request,
 * subscribing to `pushManager`, POSTing the subscription to backend,
 * and unsubscribing (client + server side).
 */
import api from "@/lib/api";

/* Convert base64url string (from VAPID public key endpoint) → Uint8Array
 * that `pushManager.subscribe()` accepts. */
export function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

export async function ensureRegistration() {
  if (!pushSupported()) throw new Error("Push not supported in this browser");
  let reg = await navigator.serviceWorker.getRegistration("/sw.js");
  if (!reg) reg = await navigator.serviceWorker.register("/sw.js");
  // Wait for it to become active if it's still installing.
  if (reg.installing) {
    await new Promise((resolve) => {
      reg.installing.addEventListener("statechange", (e) => {
        if (e.target.state === "activated") resolve();
      });
    });
  }
  return reg;
}

export async function requestPermission() {
  if (!("Notification" in window)) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  const r = await Notification.requestPermission();
  return r; // "granted" | "denied" | "default"
}

export async function subscribeToPush() {
  const reg = await ensureRegistration();
  const { data } = await api.get("/notifications/push/vapid-public-key");
  const applicationServerKey = urlBase64ToUint8Array(data.key);
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
  }
  const json = sub.toJSON();
  await api.post("/notifications/push/subscribe", {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    user_agent: navigator.userAgent,
  });
  return sub;
}

export async function unsubscribeFromPush() {
  if (!pushSupported()) return { ok: false };
  const reg = await navigator.serviceWorker.getRegistration("/sw.js");
  if (!reg) return { ok: true };
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { ok: true };
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  try {
    await api.delete("/notifications/push/unsubscribe", { data: { endpoint } });
  } catch (_) { /* server may already have it disabled — ignore */ }
  return { ok: true };
}

export async function currentPermission() {
  if (!("Notification" in window)) return "unsupported";
  return Notification.permission; // "granted" | "denied" | "default"
}

export async function hasActivePushSubscription() {
  if (!pushSupported()) return false;
  const reg = await navigator.serviceWorker.getRegistration("/sw.js");
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  return !!sub;
}

/* -------------- CTA dismissal (7-day) -------------- */
const DISMISS_KEY = "sharda_push_cta_dismissed_until";

export function dismissCtaFor7Days() {
  const until = Date.now() + 7 * 24 * 60 * 60 * 1000;
  localStorage.setItem(DISMISS_KEY, String(until));
}

export function isCtaDismissed() {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const until = parseInt(raw, 10);
  if (!until || Number.isNaN(until)) return false;
  return Date.now() < until;
}

export function clearCtaDismissal() {
  localStorage.removeItem(DISMISS_KEY);
}
